import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { DefaultRootState } from 'react-redux'

import { animeSubmissionAPI, mediaAPI } from '@services/api'

const initAnimeSubscriptionDetail: TAnimeSubscription.State['animeSubscriptionDetail']['data'] = {
  ApprovalUserName: '',
  commentToApprover: '',
  description: '',
  id: '',
  imgThumb: '',
  orderNumber: '',
  status: '',
  storeId: '',
  storeName: '',
  title: '',
  uploadedTime: '',
  url: '',
  videoName: '',
  approvalUserId: '',
  video: {
    fileSize: 0,
    originalName: '',
    filePath: '',
    uniqueName: ''
  },
  approver: '',
  approverId: '',
  commentId: '',
  editorMessage: '',
  firstDishIntroductionVideoUrl: '',
  firstDishVideoSubTitle: '',
  firstDishIntroductionVideoId: '',
  firstDishIntroductionVideoDownloadUrl: '',
  latestSubmissionDate: '',
  premiumVideoDescription: '',
  premiumVideoTitle: '',
  premiumVideoUrl: '',
  rejectReason: '',
  requestDate: '',
  secondDishIntroductionVideoUrl: '',
  secondDishVideoSubTitle: '',
  secondDishVideoId: '',
  secondDishIntroductionVideoDownloadUrl: '',
  storeManagerIntroductionVideoUrl: '',
  storeManagerVideoSubTitle: '',
  storeManagerVideoId: '',
  storeManagerIntroductionVideoDownloadUrl: '',
  storeCD: '',
  branchName: ''
}

const initAnimeSubmissionsListData: TAnimeSubscription.State['animeSubmissionsListData'] = {
  data: {
    data: []
  },
  pageIndex: 1,
  pageSize: 20,
  totalRecords: 0,
  totalPage: 1,
  hasNextPage: false,
  hasPreviousPage: false
}

const initVideoSubmissionsListData: TAnimeSubscription.State['videoSubmissionsListData'] = {
  data: {
    data: []
  },
  pageIndex: 1,
  pageSize: 20,
  totalRecords: 0,
  totalPage: 1,
  hasNextPage: false,
  hasPreviousPage: false
}

const initialState: TAnimeSubscription.State = ({
  videoSubmissionsListData: initVideoSubmissionsListData,
  animeSubmissionsListData: initAnimeSubmissionsListData,
  animeSubmissionsListOriginalData: initAnimeSubmissionsListData,
  isfetchingSubmissions: false,
  animeSubscriptionDetail: {
    data: initAnimeSubscriptionDetail
  },
  originalAnimeSubscriptionDetail: {
    data: initAnimeSubscriptionDetail
  },
  approvers: {
    data: []
  },
  historyRequest: {
    data: []
  },
  filter: {
    from: '',
    to: '',
    Term: '',
    Status: '',
    PageIndex: 1,
    PageSize: 20,
    OrderField: '',
    OrderDirection: ''
  },
  uploadVideoUrl: '',
  uploadVideoFile: null,
  selectedApprover: '',
  isCanNotChangeOrderNumber: false,
  isShowRequestVideo: false,
  isShowHistoryRequest: false,
  isLoading: false,
  showDeleteConfirmModal: false
})

export const fetchAnimeSubscriptions = createAsyncThunk(
  'animeSubscription/fetchList',
  async (params: TAnimeSubscriptionAPI.Params.FetchList, { rejectWithValue }) => {
    try {
      const response = await animeSubmissionAPI.fetchList(params)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const fetchVideoSubscriptions = createAsyncThunk(
  'animeSubscription/fetchVideoList',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as DefaultRootState
      const response = await animeSubmissionAPI.fetchVideoList(state.animeSubscription.filter)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const fetchSingle = createAsyncThunk(
  'animeSubscription/fetchSingle',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await animeSubmissionAPI.fetchSingle(id)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const fetchHistory = createAsyncThunk(
  'animeSubscription/fetchHistory',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await animeSubmissionAPI.fetchSingleHistory(id)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const cancelAnime = createAsyncThunk(
  'animeSubscription/cancelAnime',
  async (id: string) => {
    try {
      const response = await animeSubmissionAPI.cancel({ id })
      return response.data
    } catch (error: any) {
      return error
    }
  }
)

export const deleteAnime = createAsyncThunk(
  'animeSubscription/deleteAnime',
  async (id: string) => {
    try {
      const response = await animeSubmissionAPI.delete({ id })
      return response.data
    } catch (error: any) {
      return error
    }
  }
)

export const fetchAprroverList = createAsyncThunk(
  'animeSubscription/fetchAprroverList',
  async (_, { rejectWithValue }) => {
    try {
      const response = await animeSubmissionAPI.fetchAprroverList()
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const saveAnimeSubmissionTemporary = createAsyncThunk(
  'animeSubscription/saveAnimeSubmissionTemporary',
  async (data: TAnimeSubscriptionAPI.Body.SaveAnimeSubmission, { getState }) => {
    const state = getState() as DefaultRootState
    try {
      if (state.animeSubscription.uploadVideoFile) {
        const presignedInfo = await mediaAPI.createPresignedUrl({
          DataType: 'Video',
          FileName: state.animeSubscription.uploadVideoFile.name,
          StoreName: state.animeSubscription.animeSubscriptionDetail.data.storeName
        })
        const { presignedUrl, name, key } = presignedInfo.data.data
        await mediaAPI.uploadFileS3({
          url: presignedUrl,
          data: state.animeSubscription.uploadVideoFile,
          fileType: state.animeSubscription.uploadVideoFile.type
        })
        data.video = {
          fileSize: state.animeSubscription.uploadVideoFile.size,
          originalName: name,
          presignedUrlKey: key
        }
      }
      const response = await animeSubmissionAPI.saveTemporary(data)
      return response.data
    } catch (error: any) {
      return error
    }
  }
)

export const submitForApproval = createAsyncThunk(
  'animeSubscription/submitForApproval',
  async (data: TAnimeSubscriptionAPI.Body.SubmitForApprovalAnime, { getState }) => {
    const state = getState() as DefaultRootState
    try {
      if (state.animeSubscription.uploadVideoFile) {
        const presignedInfo = await mediaAPI.createPresignedUrl({
          DataType: 'Video',
          FileName: state.animeSubscription.uploadVideoFile.name,
          StoreName: state.animeSubscription.animeSubscriptionDetail.data.storeName
        })
        const { presignedUrl, name, key } = presignedInfo.data.data
        await mediaAPI.uploadFileS3({
          url: presignedUrl,
          data: state.animeSubscription.uploadVideoFile,
          fileType: state.animeSubscription.uploadVideoFile.type
        })
        data.video = {
          fileSize: state.animeSubscription.uploadVideoFile.size,
          originalName: name,
          presignedUrlKey: key
        }
      }
      const response = await animeSubmissionAPI.submitForApproval(data)
      return response.data
    } catch (error: any) {
      return error
    }
  }
)

export const submitCreateVideo = createAsyncThunk(
  'animeSubscription/submitCreateVideo',
  async (data: any) => {
    try {
      const response = await animeSubmissionAPI.createVideoPremium(data)
      return response.data
    } catch (error: any) {
      return error
    }
  }
)

const animeSubscriptionSlice = createSlice({
  name: 'animeSubscription',
  initialState,
  reducers: {
    setIsfetchingSubmissions (state, action: PayloadAction<{isfetching: boolean}>) {
      state.isfetchingSubmissions = action.payload.isfetching
    },
    setFieldsInSingleAnimeSubmission (
      state,
      action: PayloadAction<{ field: keyof TAnimeSubscription.State['animeSubscriptionDetail']['data'], value: any }>
    ) {
      const { field, value } = action.payload
      state.animeSubscriptionDetail.data[field] = value
    },
    setSelectedApprover (state, action: PayloadAction<string>) {
      state.selectedApprover = action.payload
    },
    clearDirtyDetailForm (state) {
      state.animeSubscriptionDetail.data = state.originalAnimeSubscriptionDetail.data
    },
    setOriginalAnimeSubscriptions (state) {
      state.animeSubmissionsListData = state.animeSubmissionsListOriginalData
    },
    changeShowRequest (state) {
      state.isShowRequestVideo = !state.isShowRequestVideo
    },
    changeShowHistoryRequest (state) {
      state.isShowHistoryRequest = !state.isShowHistoryRequest
    },
    setFilter (state, action: PayloadAction<{ field: Partial<keyof TAnimeSubscription.State['filter']>, value: any }>) {
      const { field, value } = action.payload
      // @ts-ignore
      state.filter[field] = value
    },
    changeVideo: (state, action: PayloadAction<File>) => {
      state.uploadVideoFile = action.payload
      state.uploadVideoUrl = URL.createObjectURL(action.payload)
    },
    removeVideo: state => {
      state.uploadVideoFile = null
      state.uploadVideoUrl = ''
    },
    setLoading (state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload
    },
    setShowDeleteConfirmModal (state, action: PayloadAction<boolean>) {
      state.showDeleteConfirmModal = action.payload
    },
    setSearchTerm (state, action: PayloadAction<string>) {
      state.filter.Term = action.payload
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAnimeSubscriptions.fulfilled, (state, action: PayloadAction<TAnimeSubscription.State['animeSubmissionsListData']>) => {
        if (!action.payload) return
        state.animeSubmissionsListData = action.payload
        if (!state.animeSubmissionsListOriginalData.data?.data.length) {
          state.animeSubmissionsListOriginalData = action.payload
        }
        state.isfetchingSubmissions = false
      })
      .addCase(fetchAnimeSubscriptions.pending, state => {
        state.isfetchingSubmissions = true
      })
    builder
      .addCase(fetchVideoSubscriptions.fulfilled, (state, action: PayloadAction<TAnimeSubscription.State['videoSubmissionsListData']>) => {
        if (!action.payload) return
        state.videoSubmissionsListData = action.payload
        state.isfetchingSubmissions = false
      })
      .addCase(fetchVideoSubscriptions.pending, state => {
        state.isfetchingSubmissions = true
      })
    builder
      .addCase(fetchSingle.fulfilled, (state, action: PayloadAction<TAnimeSubscription.State['animeSubscriptionDetail']>) => {
        state.isLoading = false
        if (!action.payload?.data) return
        state.animeSubscriptionDetail.data = action.payload.data
        state.uploadVideoUrl = action.payload.data.premiumVideoUrl
        state.selectedApprover = action.payload.data.approverId ?? ''
        state.originalAnimeSubscriptionDetail.data = action.payload.data
        state.originalAnimeSubscriptionDetail.data.title = action.payload.data.premiumVideoTitle ?? ''
        state.originalAnimeSubscriptionDetail.data.description = action.payload.data.premiumVideoDescription ?? ''
        state.originalAnimeSubscriptionDetail.data.commentToApprover = action.payload.data.editorMessage ?? ''
        state.isCanNotChangeOrderNumber = !!action.payload.data.orderNumber
      })
      .addCase(fetchSingle.pending, state => {
        state.isLoading = true
      })
    builder
      .addCase(fetchHistory.fulfilled, (state, action: PayloadAction<TAnimeSubscription.State['historyRequest']>) => {
        if (!action.payload?.data) return
        state.historyRequest.data = action.payload.data
      })
    builder
      .addCase(saveAnimeSubmissionTemporary.fulfilled, state => {
        state.isLoading = false
      })
      .addCase(saveAnimeSubmissionTemporary.pending, state => {
        state.isLoading = true
      })
    builder
      .addCase(submitForApproval.fulfilled, state => {
        state.isLoading = false
      })
      .addCase(submitForApproval.pending, state => {
        state.isLoading = true
      })
    builder
      .addCase(submitCreateVideo.fulfilled, state => {
        state.isLoading = false
      })
      .addCase(submitCreateVideo.pending, state => {
        state.isLoading = true
      })
    builder
      .addCase(fetchAprroverList.fulfilled, (state, action: any) => {
        if (!action.payload?.data) return
        state.approvers.data = action.payload.data
        if (action.payload.data.length) state.selectedApprover = action.payload.data[0]
      })
  }
})

export const {
  clearDirtyDetailForm,
  setFieldsInSingleAnimeSubmission,
  setSelectedApprover,
  setOriginalAnimeSubscriptions,
  changeShowRequest,
  changeShowHistoryRequest,
  setFilter,
  changeVideo,
  setLoading,
  removeVideo,
  setShowDeleteConfirmModal,
  setSearchTerm
} = animeSubscriptionSlice.actions

export const animeSubscriptionReducer = animeSubscriptionSlice.reducer
export default animeSubscriptionSlice.reducer
